import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FormEvent,
} from 'react';
import { cn } from '@/lib/utils/cn';

type Props = {
  /** Current value reflected from the URL. */
  value: string;
  /** Called when the user "commits" the query (debounced typing, ENTER, or blur). */
  onCommit: (next: string) => void;
  placeholder?: string;
  autoFocus?: boolean;
  debounceMs?: number;
};

/**
 * Search input.
 *
 * - Maintains a local draft so typing is responsive even while a parent
 *   URL update is in flight.
 * - Debounces commits to `onCommit` so URL writes don't spam history.
 * - ENTER commits immediately. ESC clears.
 * - ⌘K / Ctrl+K from anywhere on the page focuses the input.
 */
export function SearchBox({
  value,
  onCommit,
  placeholder = 'Search the Quran…',
  autoFocus = true,
  debounceMs = 350,
}: Props) {
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const timerRef = useRef<number | null>(null);

  // Reconcile draft when the parent value changes (e.g. back button).
  useEffect(() => {
    setDraft(value);
  }, [value]);

  // Debounced commit.
  useEffect(() => {
    if (draft === value) return;
    if (timerRef.current) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      onCommit(draft);
    }, debounceMs);
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
    };
  }, [draft, value, onCommit, debounceMs]);

  // Cmd+K / Ctrl+K focuses from anywhere.
  useEffect(() => {
    function handler(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    }
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const handleSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      if (timerRef.current) window.clearTimeout(timerRef.current);
      onCommit(draft);
    },
    [draft, onCommit],
  );

  const handleClear = useCallback(() => {
    setDraft('');
    onCommit('');
    inputRef.current?.focus();
  }, [onCommit]);

  return (
    <form role="search" onSubmit={handleSubmit}>
      <label className="block relative">
        <span className="sr-only">Search the Quran</span>
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 left-4 flex items-center text-ink-400 dark:text-ink-500"
        >
          <SearchIcon />
        </span>
        <input
          ref={inputRef}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          autoFocus={autoFocus}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              handleClear();
            }
          }}
          placeholder={placeholder}
          className={cn(
            'w-full h-12 sm:h-14 pl-12 pr-24',
            'bg-white dark:bg-ink-900',
            'border border-ink-200 dark:border-ink-800',
            'rounded-2xl',
            'text-base sm:text-lg text-ink-900 dark:text-ink-50',
            'placeholder:text-ink-400 dark:placeholder:text-ink-500',
            'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40',
            'transition-colors',
          )}
        />
        <span className="absolute inset-y-0 right-3 flex items-center gap-2">
          {draft && (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear search"
              className="h-7 w-7 inline-flex items-center justify-center rounded-md text-ink-400 hover:text-ink-700 dark:hover:text-ink-200 transition-colors"
            >
              <CloseIcon />
            </button>
          )}
          <kbd
            aria-hidden="true"
            className="hidden sm:inline-flex h-6 px-1.5 items-center justify-center rounded border border-ink-200 dark:border-ink-700 text-[10px] font-medium text-ink-400 dark:text-ink-500 bg-ink-50 dark:bg-ink-900"
          >
            ⌘K
          </kbd>
        </span>
      </label>
    </form>
  );
}

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}
